<script setup lang="ts">
import { computed } from 'vue'
import DreamaProgressiveImage from '@/components/dreama-progressive-image'
import type { Props } from './type'

defineOptions({
  name: 'MessageHistoryCard',
})

const props = withDefaults(defineProps<Props>(), {
  variant: 'archive',
})

defineEmits<{
  (e: 'select', item: Props['item']): void
}>()

const avatarFallback = computed(() => props.item.cartoonName.slice(0, 1) || '梦')
const unreadLabel = computed(() => (props.item.unreadCount > 99 ? '99+' : String(props.item.unreadCount)))
</script>

<template>
  <article
    class="message-card"
    :class="{
      'is-unread': item.hasUnread,
      'is-failed': item.status === 0,
    }"
    @click="$emit('select', item)"
  >
    <DreamaProgressiveImage
      class="message-card__cover-shell"
      :src="item.imageUrl"
      alt=""
      :preview-width="48"
    >
      <div class="message-card__cover-top">
        <div class="message-card__identity">
          <span class="message-card__avatar-shell">
            <img
              v-if="item.cartoonAvatar"
              class="message-card__avatar"
              :src="item.cartoonAvatar"
              :alt="item.cartoonName"
            />
            <span v-else class="message-card__avatar-fallback">{{ avatarFallback }}</span>
          </span>

          <div class="message-card__identity-copy">
            <span class="message-card__cartoon">{{ item.cartoonName }}</span>
          </div>
        </div>
      </div>

      <div class="message-card__cover-bottom">
        <h3 class="message-card__title">{{ item.title }}</h3>
      </div>

      <span v-if="item.hasUnread" class="message-card__unread-badge">{{ unreadLabel }}</span>
    </DreamaProgressiveImage>
  </article>
</template>

<style scoped>
.message-card {
  position: relative;
  isolation: isolate;
  display: flex;
  flex-direction: column;
  aspect-ratio: 244 / 354.909;
  padding: 12px;
  border-radius: 0;
  background: transparent;
  cursor: pointer;
  transition: opacity 180ms ease;
}

.message-card::before {
  position: absolute;
  inset: 4px;
  z-index: -1;
  border-radius: 20px;
  background-color: var(--app-state-layer-hover);
  content: '';
  opacity: 0;
  transform: scale(var(--app-interaction-media-rest-scale));
  transition:
    opacity 0.18s ease,
    transform 0.18s ease;
}

.message-card:hover::before,
.message-card:focus-visible::before {
  opacity: 1;
  transform: scale(var(--app-interaction-media-reveal-scale));
}

.message-card__cover-shell {
  position: relative;
  overflow: hidden;
  height: 100%;
  flex: 1 1 auto;
  border-radius: 12px;
  background: var(--app-skeleton-base);
  box-shadow: var(--app-shadow-soft);
  --dreama-image-placeholder-bg: var(--app-skeleton-base);
}

.message-card__cover-shell::after {
  position: absolute;
  inset: 0;
  z-index: 2;
  background: rgb(12 12 16 / 0.1);
  opacity: 0;
  transition: opacity 180ms ease;
  content: '';
  pointer-events: none;
}

.message-card:hover .message-card__cover-shell::after,
.message-card:focus-visible .message-card__cover-shell::after {
  opacity: 1;
}

.message-card:active .message-card__cover-shell::after {
  background: rgb(12 12 16 / 0.16);
  opacity: 1;
}

.message-card__cover-top,
.message-card__cover-bottom {
  position: absolute;
  left: 0;
  right: 0;
  z-index: 1;
}

.message-card__cover-top {
  top: 0;
  padding: 8px;
  background: linear-gradient(180deg, rgb(0 0 0 / 0.5), transparent 85%);
}

.message-card__cover-bottom {
  bottom: 0;
  padding: 20px 10px 10px;
  background: linear-gradient(180deg, transparent, rgb(0 0 0 / 0.55));
}

.message-card__identity {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 8px;
}

.message-card__avatar-shell {
  display: grid;
  width: 40px;
  height: 40px;
  flex: 0 0 auto;
  place-items: center;
  overflow: hidden;
  border-radius: 999px;
  background: rgb(255 255 255 / 0.18);
  box-shadow: inset 0 0 0 1px rgb(255 255 255 / 0.22);
  backdrop-filter: blur(10px);
}

.message-card__avatar,
.message-card__avatar-fallback {
  width: 100%;
  height: 100%;
}

.message-card__avatar {
  object-fit: cover;
}

.message-card__avatar-fallback {
  display: grid;
  place-items: center;
  color: white;
  font-size: 15px;
  font-weight: 700;
}

.message-card__identity-copy {
  display: flex;
  min-width: 0;
  flex-direction: column;
}

.message-card__cartoon,
.message-card__title {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.message-card__cartoon {
  color: white;
  font-size: 14px;
  font-weight: 700;
}

.message-card__title {
  margin: 0;
  color: white;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.35;
}

.message-card__unread-badge {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 1;
  display: inline-flex;
  min-width: 32px;
  height: 32px;
  align-items: center;
  justify-content: center;
  padding: 0 7px;
  border-radius: 22px;
  background: var(--distribute-accent);
  color: white;
  font-size: 12px;
  font-weight: 700;
  box-shadow: 0 10px 22px rgb(15 179 109 / 0.28);
}

</style>
